import { useNavigate } from "react-router-dom";
import { ArrowRight, CloudArrowDown, PencilSimple, ChartLine, Folder, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/App";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b-2 border-[#E4E4E7] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/images/logo-light.png" 
              alt="CauraTech" 
              className="h-14 w-auto"
            />
          </div>
          <button
            data-testid="header-get-started-btn"
            onClick={handleGetStarted}
            className="btn-industrial text-sm"
          >
            {user ? "Panel" : "Comenzar"}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative border-b-2 border-[#E4E4E7] overflow-hidden">
        {/* Background gradient */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            background: 'linear-gradient(135deg, #003594 0%, #008AD8 50%, #057EF3 100%)',
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <p className="label-overline mb-4 text-[#003594]">Generador de Códigos QR Profesionales</p>
            <h1 
              className="text-5xl lg:text-7xl font-black leading-none mb-6"
              style={{ color: '#051C2C' }}
            >
              CÓDIGOS QR
              <br />
              <span style={{ color: '#003594' }}>DE GRADO INDUSTRIAL</span>
            </h1>
            <p className="text-lg text-[#52525B] mb-8 max-w-xl leading-relaxed">
              Genera códigos QR de alta resolución listos para impresión de gran formato. 
              Vectores SVG, exportación a 300 DPI y gestión dinámica de URLs en una sola plataforma.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-get-started-btn"
                onClick={handleGetStarted}
                className="btn-industrial flex items-center gap-2"
              >
                Empezar a Crear
                <ArrowRight size={20} weight="bold" />
              </button>
              <a
                href="#features"
                className="btn-industrial-outline"
              >
                Saber Más
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-b-2 border-[#E4E4E7] bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Formatos de Exportación", value: "SVG + PNG" },
            { label: "Resolución", value: "300 DPI" },
            { label: "Corrección de Errores", value: "Nivel H" },
            { label: "Soporte de Logo", value: "SÍ" },
          ].map((stat, index) => (
            <div 
              key={index} 
              className={`p-6 text-center ${index < 3 ? 'border-r-2 border-[#E4E4E7]' : ''}`}
            >
              <p className="label-overline mb-1">{stat.label}</p>
              <p className="font-bold text-2xl text-[#003594]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Bento Grid */}
      <section id="features" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <p className="label-overline mb-4 text-[#003594]">Capacidades</p>
          <h2 
            className="text-3xl lg:text-5xl font-black mb-12"
            style={{ color: '#051C2C' }}
          >
            DISEÑADO PARA PRODUCCIÓN
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-2 border-[#003594]">
            {/* High Resolution Export */}
            <div className="border-b-2 lg:border-b-0 lg:border-r-2 border-[#003594] p-8 bg-white">
              <div className="w-12 h-12 bg-[#003594] flex items-center justify-center mb-6">
                <CloudArrowDown size={24} weight="bold" className="text-white" />
              </div>
              <h3 className="font-bold text-xl mb-3 text-[#051C2C]">
                Exportación de Alta Resolución
              </h3>
              <p className="text-[#52525B] leading-relaxed mb-4">
                Exporta vectores SVG limpios para escalado ilimitado. Exportación PNG a 300 DPI 
                para producción de impresión profesional.
              </p>
              <div className="h-32 overflow-hidden">
                <img 
                  src="/images/feature-high-res.png" 
                  alt="Exportación de alta resolución"
                  className="w-full h-full object-cover opacity-90"
                />
              </div>
            </div>

            {/* Dynamic URL Management */}
            <div className="border-b-2 lg:border-b-0 lg:border-r-2 border-[#003594] p-8 bg-white">
              <div className="w-12 h-12 bg-[#003594] flex items-center justify-center mb-6">
                <PencilSimple size={24} weight="bold" className="text-white" />
              </div>
              <h3 className="font-bold text-xl mb-3 text-[#051C2C]">
                Edición Dinámica de URLs
              </h3>
              <p className="text-[#52525B] leading-relaxed mb-4">
                Cambia las URLs de destino en cualquier momento sin reimprimir. Tus códigos QR 
                permanecen iguales mientras la redirección se actualiza al instante.
              </p>
              <div className="h-32 overflow-hidden">
                <img 
                  src="/images/feature-dynamic-urls.png" 
                  alt="Edición dinámica de URLs"
                  className="w-full h-full object-cover opacity-90"
                />
              </div>
            </div>

            {/* Analytics */}
            <div className="p-8 bg-white">
              <div className="w-12 h-12 bg-[#003594] flex items-center justify-center mb-6">
                <ChartLine size={24} weight="bold" className="text-white" />
              </div>
              <h3 className="font-bold text-xl mb-3 text-[#051C2C]">
                Analíticas de Escaneo
              </h3>
              <p className="text-[#52525B] leading-relaxed mb-4">
                Rastrea cada escaneo con analíticas detalladas. Ve tipos de dispositivo (iOS/Android), 
                distribución geográfica y tendencias diarias.
              </p>
              <div className="grid grid-cols-3 gap-2 mt-6">
                {["iOS", "Android", "País"].map((item) => (
                  <div key={item} className="bg-[#003594] bg-opacity-10 p-3 text-center">
                    <p className="text-xs text-[#003594]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-x-2 border-b-2 border-[#003594]">
            {/* Project Organization */}
            <div className="border-b-2 lg:border-b-0 lg:border-r-2 border-[#003594] p-8 bg-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#003594] flex items-center justify-center flex-shrink-0">
                  <Folder size={24} weight="bold" className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3 text-[#051C2C]">
                    Organización por Proyectos
                  </h3>
                  <p className="text-[#52525B] leading-relaxed">
                    Organiza los códigos QR en proyectos. Perfecto para gestionar múltiples campañas, 
                    clientes o ubicaciones sin colisiones.
                  </p>
                </div>
              </div>
            </div>

            {/* Logo Support */}
            <div className="p-8 bg-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#003594] flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={24} weight="bold" className="text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3 text-[#051C2C]">
                    Inserción de Logo
                  </h3>
                  <p className="text-[#52525B] leading-relaxed">
                    Añade el logo de tu empresa en el centro de los códigos QR. La alta corrección 
                    de errores (Nivel H) garantiza legibilidad incluso con logos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t-2 border-[#E4E4E7] py-16 lg:py-24" style={{ backgroundColor: '#05152E' }}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 
            className="text-3xl lg:text-5xl font-black text-white mb-6"
          >
            ¿LISTO PARA CREAR?
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-8 max-w-xl mx-auto">
            Comienza a generar códigos QR profesionales en minutos. Sin tarjeta de crédito requerida.
          </p>
          <button
            data-testid="cta-get-started-btn"
            onClick={handleGetStarted}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white font-bold text-lg border-2 border-white hover:bg-[#F4F4F5] transition-colors"
            style={{ color: '#003594' }}
          >
            Comenzar Gratis
            <ArrowRight size={24} weight="bold" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#E4E4E7] bg-white py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="/images/logo-light.png" 
              alt="CauraTech" 
              className="h-10 w-auto"
            />
          </div>
          <p className="text-sm text-[#A1A1AA]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            2026 — CauraTech | Códigos QR de Grado Industrial
          </p>
        </div>
      </footer>
    </div>
  );
}
